import { getPayslipDeliveryMethodOptions } from '@Component/AttributeDropdown/AttributeDropdown';
import { FilterState, focusOnSearchFilter, textAll, textMultiple, textPlaceholder } from '@Component/Filter/utils';
import { usePagesPilotFeatureFlags } from '@Hooks/dataFetching/usePagesPilotFeatureFlags';
import { useKeyboardShortcut } from '@Hooks/useKeyboardShortcut';
import { AUTH_LEVEL_DEPARTMENT, useTskContext } from '@Hooks/useTskContext.ts';
import { TaxcardYearDropdownOptions } from '@Page/EmployeeOverview/Taxcard/TaxcardYearDropdownOptions';
import {
    AsyncDropdownOptions,
    DropdownFilter,
    FilterItem,
    Filters,
    RadioGroupFilter,
    SearchFilter,
    ToggleFilter,
} from '@tlx/astro-shared';
import { Option } from '@tlx/atlas';
import { SEARCH_KEY_COMBINATIONS } from '@Util/getKeyboardEventKeybinding';
import { DropdownUrls } from '@Util/urls';
import { useIntl } from 'react-intl';

interface Props {
    filterState: FilterState;
}

export function EmployeeOverviewFilters({ filterState }: Props) {
    const intl = useIntl();
    const textNone = intl.formatMessage({ id: 'option_none' });
    const { authLevelEmployeeOrWage, hasDepartment, hasEmployeeCategory, userDepartmentId, hasDivision } =
        useTskContext();

    const { data } = usePagesPilotFeatureFlags();
    const hasAttestation = data?.value.Attestation;

    //focus on the searchFilter when people do the usual browser search (which wont work because we lazy load the table and therefor all the text is not there to be searched yet)
    useKeyboardShortcut(SEARCH_KEY_COMBINATIONS, focusOnSearchFilter);

    const departmentFilterDisabled =
        filterState.viewFilter.query === 'salary' &&
        authLevelEmployeeOrWage == AUTH_LEVEL_DEPARTMENT &&
        userDepartmentId != 0 &&
        hasDepartment;

    return (
        <Filters>
            <SearchFilter {...filterState.searchFilter} />
            {authLevelEmployeeOrWage >= AUTH_LEVEL_DEPARTMENT && (
                <RadioGroupFilter {...filterState.viewFilter} defaultValue={filterState.viewFilter.defaultValue?.[0]}>
                    <FilterItem value="details">{intl.formatMessage({ id: 'text_details' })}</FilterItem>
                    <>
                        {authLevelEmployeeOrWage >= AUTH_LEVEL_DEPARTMENT && (
                            <FilterItem value="salary">{intl.formatMessage({ id: 'menu_wage' })}</FilterItem>
                        )}
                    </>
                    {/* <FilterItem value="taxcard">
                    {intl.formatMessage({ id: 'text_tax_deduction_card' })}
                </FilterItem> */}
                </RadioGroupFilter>
            )}
            {/* some year filter here only when taxcard tab? */}
            {filterState.viewFilter.query === 'taxcard' && (
                <DropdownFilter
                    {...filterState.taxcardYearFilter}
                    defaultValue={filterState.taxcardYearFilter.defaultValue?.[0]}
                >
                    <TaxcardYearDropdownOptions />
                </DropdownFilter>
            )}
            {hasDepartment && (
                <DropdownFilter
                    {...filterState.departmentFilter}
                    defaultDisplayName={textAll(filterState.departmentFilter, intl)}
                    multiple
                    placeholder={textPlaceholder(filterState.departmentFilter, intl)}
                    disabled={departmentFilterDisabled}
                    selectedItemsLabel={(count) => textMultiple(filterState.departmentFilter, count)}
                >
                    <></>
                    <AsyncDropdownOptions url={DropdownUrls.ACTIVE_DEPARTMENTS} />
                </DropdownFilter>
            )}
            {authLevelEmployeeOrWage >= AUTH_LEVEL_DEPARTMENT && (
                <>
                    {hasEmployeeCategory && (
                        <DropdownFilter
                            {...filterState.employeeCategoryFilter}
                            defaultDisplayName={textAll(filterState.employeeCategoryFilter, intl)}
                            multiple
                            placeholder={textPlaceholder(filterState.employeeCategoryFilter, intl)}
                            selectedItemsLabel={(count) => textMultiple(filterState.employeeCategoryFilter, count)}
                        >
                            <Option value="-2">{textNone}</Option>
                            <AsyncDropdownOptions url={DropdownUrls.EMPLOYEE_CATEGORIES} />
                        </DropdownFilter>
                    )}
                    {hasAttestation && (
                        <DropdownFilter
                            {...filterState.employeeAttestantsFilter}
                            defaultDisplayName={textAll(filterState.employeeAttestantsFilter, intl)}
                            multiple
                            placeholder={textPlaceholder(filterState.employeeAttestantsFilter, intl)}
                            selectedItemsLabel={(count) => textMultiple(filterState.employeeAttestantsFilter, count)}
                        >
                            <Option value="0">{textNone}</Option>
                            <Option value="-1">{`(${intl.formatMessage({ id: 'text_department_manager' })})`}</Option>
                            <AsyncDropdownOptions url={DropdownUrls.EMPLOYEES} />
                        </DropdownFilter>
                    )}
                    {hasDivision && (
                        <DropdownFilter
                            {...filterState.divisionFilter}
                            defaultDisplayName={textAll(filterState.divisionFilter, intl)}
                            multiple
                            placeholder={textPlaceholder(filterState.divisionFilter, intl)}
                            selectedItemsLabel={(count) => textMultiple(filterState.divisionFilter, count)}
                        >
                            <></>
                            <AsyncDropdownOptions url={DropdownUrls.DIVISIONS} />
                        </DropdownFilter>
                    )}
                    <DropdownFilter
                        {...filterState.deliveryMethodWageSlipListFilter}
                        defaultDisplayName={textAll(filterState.deliveryMethodWageSlipListFilter, intl)}
                        multiple
                        placeholder={textPlaceholder(filterState.deliveryMethodWageSlipListFilter, intl)}
                        selectedItemsLabel={(count) =>
                            textMultiple(filterState.deliveryMethodWageSlipListFilter, count)
                        }
                    >
                        {getPayslipDeliveryMethodOptions(intl).map((option) => (
                            <Option key={option.id} value={option.id}>
                                {option.text}
                            </Option>
                        ))}
                    </DropdownFilter>
                </>
            )}
            <ToggleFilter {...filterState.employeeAvailabilityFilter} />
            {authLevelEmployeeOrWage >= AUTH_LEVEL_DEPARTMENT && (
                <>
                    <ToggleFilter {...filterState.salaryContactsFilter} />
                    <ToggleFilter {...filterState.systemAccessContactsFilter} />
                    <ToggleFilter {...filterState.onlySystemAccessFilter} />
                </>
            )}
        </Filters>
    );
}
